import React, {useEffect, useState} from 'react'
import {
    Charity,
    CharityBeneficiary,
    Distribution,
    DistributionType,
    FamilyMembers,
    MemberType,
    Outright,
    Trustee
} from '../../../model/EstateFlowchartTypes'
import MemberDropdownMultiselect from "../MemberDropdownMultiselect/MemberDropdownMultiselect";
import {evaluateDropdownMemberId, isCustomMember, parseFamilyMembers} from "../../../utils/DefUtils";
import {saveSelector} from "../../../slices/NavigationSlice";
import {familyMembersSelector} from "../../../slices/EstateFlowChartSlice";
import {NON_TRUST_FORM_CONSTANTS as Labels} from "../../../constants/LabelConstants";
import TextAreaField from "../TextAreaField/TextAreaField";
import FormPageSection from "../../FormPageSection/FormPageSection";
import {getSectionId} from "../../FormNav/FormNavUtils";
import {useAppSelector} from "../../../../store/hooks";
import Input from "../../../../components/Input/Input";
import {Button, DeleteButton, RedAsterisk} from "../../../../components";
import {selectReleaseToggles} from "../../../../ReleaseToggles/releaseTogglesSlice";
import InputField from "../InputField/InputField";

interface NonTrustDistributionFormProps {
    pageIndex: number;
    sectionIndex: number;
    distributionInfo: Charity | Outright;
    updateDistribution: (trust: Distribution, index: number) => void;
    deleteDistribution: (index: number) => void;
}

const CHARITY_NAME_PREFIX = "Charity:";

const getInitialCharityBeneficiaries = (distributionInfo: Charity): CharityBeneficiary[] => {
    const charityBeneficiaries = distributionInfo.charityBeneficiaries ?? [];
    const hasNamedBeneficiaries = charityBeneficiaries.some(
        (beneficiary) => beneficiary.name.trim() !== ""
    );

    if (hasNamedBeneficiaries) {
        return charityBeneficiaries;
    }

    const parsedDistributionNames = distributionInfo.name
        ?.replace(CHARITY_NAME_PREFIX, "")
        .split(",")
        .map((name) => name.trim())
        .filter((name) => name !== "") ?? [];

    if (parsedDistributionNames.length > 0) {
        return parsedDistributionNames.map((name) => ({
            charityId: "",
            name,
        }));
    }

    return [{charityId: "", name: ""}];
};

/**
 * The `NonTrustDistributionForm` component renders a form to manage the details of a non-trust distribution.
 * It supports input and updates for funding, additional details, and beneficiary information.
 *
 * @component
 * @param {NonTrustDistributionFormProps} props - The properties passed to the component.
 * @param {Charity | Outright} props.distributionInfo - Information about the distribution.
 * @param {Function} props.updateDistribution - Function to update the distribution.
 * @param {Function} props.deleteDistribution - Function to delete the distribution.
 *
 * @returns {JSX.Element} The rendered `NonTrustDistributionForm` component.
 *
 * @example
 * <NonTrustDistributionForm
 *   pageIndex={0}
 *   sectionIndex={1}
 *   distributionInfo={distributionInfo}
 *   updateDistribution={updateDistribution}
 *   deleteDistribution={deleteDistribution}
 * />
 */
function NonTrustDistributionForm({
                                      pageIndex,
                                      sectionIndex,
                                      distributionInfo,
                                      updateDistribution,
                                      deleteDistribution,
                                  }: Readonly<NonTrustDistributionFormProps>): JSX.Element {

    const saveHasBeenClicked = useAppSelector(saveSelector);
    const rawFamilyMembers = useAppSelector(familyMembersSelector);
    const releaseToggles = useAppSelector(selectReleaseToggles);

    const generateFormStyles = (distributionType: DistributionType) => {
        switch (distributionType) {
            case DistributionType.CHARITY:
                return {
                    headerText: "Charity",
                    nameLabelText: Labels.CHARITY_NAME_LABEL_TEXT,
                    icon: "heart",
                    additionalDetailsSubText: Labels.CHARITY_ADDITIONAL_DETAILS_SUB_TEXT
                };
            case DistributionType.OUTRIGHT :
                return {
                    headerText: "Outright",
                    nameLabelText: Labels.OUTRIGHT_NAME_LABEL_TEXT,
                    icon: "account_switch",
                    additionalDetailsSubText: Labels.OUTRIGHT_ADDITIONAL_DETAILS_SUB_TEXT
                }
            default:
                return {headerText: "", icon: "", nameLabelText: "", additionalDetailsSubText: ""};
        }
    }

    const defaultNonTrustDistribution = {
        distributionId: distributionInfo.distributionId,
        beneficiaryId: distributionInfo.beneficiaryId,
        sequenceNumber: distributionInfo.sequenceNumber,
        createdDate: distributionInfo.createdDate,
        name: distributionInfo.name,
        distributionType: distributionInfo.distributionType,
        funding: distributionInfo.funding ?? "",
        additionalDetails: distributionInfo.additionalDetails ?? ""
    };

    const charityBeneficiaries = getInitialCharityBeneficiaries(distributionInfo as Charity);

    const defaultCharity: Charity = {
        ...defaultNonTrustDistribution,
        charityBeneficiaries: releaseToggles?.enableEstateFlowchartMVPRelease ? charityBeneficiaries : []
    };

    const defaultOutright: Outright = {
        ...defaultNonTrustDistribution,
        outrightMembers: (distributionInfo as Outright).outrightMembers
    }

    const [distribution, setDistribution] = useState<Charity | Outright>(
        distributionInfo.distributionType === DistributionType.CHARITY ? defaultCharity : defaultOutright
    )

    const {
        headerText,
        icon,
        nameLabelText,
        additionalDetailsSubText
    } = generateFormStyles(distribution.distributionType);

    const [parsedFamilyMembers, setParsedFamilyMembers] = useState({} as FamilyMembers);
    const isOutright = distribution.distributionType === DistributionType.OUTRIGHT;

    useEffect(() => {
        const parsedFamilyMembersResponse = parseFamilyMembers(rawFamilyMembers);
        let item = {label: "", value: "", selected: false};
        item.label = parsedFamilyMembersResponse.label;
        item.value = parsedFamilyMembersResponse.value;
        parsedFamilyMembersResponse.familyMember.push(item);
        setParsedFamilyMembers(parsedFamilyMembersResponse);

        if (distribution.distributionType === DistributionType.OUTRIGHT) setDistribution({
            ...distribution,
            outrightMembers: (distributionInfo as Outright).outrightMembers
        })
    }, [rawFamilyMembers]);


    const onChangeDropdown = (selectedOptions: any) => {
        const outrightSelected = [] as Trustee[];
        let leftNameDisplay = "Outright: ";
        for (const element of selectedOptions) {
            const outright: Trustee = {
                trustId: distributionInfo.beneficiaryId ?? "",
                memberType: MemberType.Beneficiary,
                familyMember: !isCustomMember(element.value, parsedFamilyMembers),
                customName: isCustomMember(element.value, parsedFamilyMembers) ? element.label : "",
                memberId: evaluateDropdownMemberId(element, parsedFamilyMembers),
            };
            outrightSelected.push(outright);
            leftNameDisplay += (element.label + ", ")
        }
        leftNameDisplay = leftNameDisplay.slice(0, leftNameDisplay.length - 2);

        if (releaseToggles?.enableEstateFlowchartMVPRelease) {
            /*fix outright name more than DB Limit*/
            leftNameDisplay = leftNameDisplay.substring(0, 255)
        }
        setDistribution({
            ...distribution,
            name: leftNameDisplay,
            outrightMembers: outrightSelected,
        });
    }

    useEffect(() => {
        updateDistribution(distribution, sectionIndex);
    }, [distribution]);

    const onAddCharityClick = () => {
        const newCharityBeneficiary = {
            charityId: "",
            name: "",
        };
        setDistribution({
            ...distribution,
            charityBeneficiaries: [
                ...((distribution as Charity).charityBeneficiaries ?? []),
                newCharityBeneficiary,
            ],
        });
    };

    function updateCharityDistribution(updatedCharityBeneficiaries: CharityBeneficiary[]) {
        const updatedName = updatedCharityBeneficiaries
            .map((beneficiary) => beneficiary.name)
            .filter((name) => name.trim() !== "") // Exclude empty names
            .join(", ");

        setDistribution({
            ...distribution,
            name: `Charity: ${updatedName}`,
            charityBeneficiaries: updatedCharityBeneficiaries,
        });
    }

    const onChangeCharityBeneficiary = (index: number, name: string) => {
        const updatedCharityBeneficiaries = [...(distribution as Charity).charityBeneficiaries];
        updatedCharityBeneficiaries[index] = {
            ...updatedCharityBeneficiaries[index],
            name,
        };
        updateCharityDistribution(updatedCharityBeneficiaries);
    };

    const onDeleteCharityBeneficiary = (index: number) => {
        const updatedCharityBeneficiaries = [...(distribution as Charity).charityBeneficiaries];
        updatedCharityBeneficiaries.splice(index, 1);
        updateCharityDistribution(updatedCharityBeneficiaries);
    };

    function renderCharityNameInputs() {
        if (!releaseToggles?.enableEstateFlowchartMVPRelease) {
            return (
                <InputField
                    label={Labels.CHARITY_NAME_LABEL_TEXT}
                    name={distribution.name}
                    required
                    onChange={(name) => setDistribution({...distribution, name})}
                    liveUpdate
                />
            );
        }

        const hasValidationError =
            saveHasBeenClicked &&
            !(distribution as Charity).charityBeneficiaries.some(
                (beneficiary) => beneficiary.name.trim() !== ""
            );

        return (
            <section
                aria-label="Charity Beneficiaries Section"
                className="charity-beneficiaries-section custom-dds-container"
            >
                <div className="add-charity-header">
                    <label>
                        Charity<RedAsterisk/>
                    </label>
                    <Button
                        onClick={onAddCharityClick}
                        icon="left"
                        iconName="add"
                        kind="borderless"
                        size="medium"
                    >
                        Add Charity
                    </Button>
                </div>
                <div className="data-form-table">
                    <div className="data-form-table-header">Beneficiaries</div>
                    {(distribution as Charity).charityBeneficiaries.map(
                        (charityBeneficiary, index) => (
                            <div
                                className="charity-beneficiary-row"
                                key={`charity-beneficiary-${charityBeneficiary.id ?? index}`}
                            >
                                <Input
                                    aria-label={`${Labels.CHARITY_NAME_LABEL_TEXT} ${index}`}
                                    value={charityBeneficiary.name}
                                    placeholder={Labels.CHARITY_NAME_LABEL_TEXT}
                                    className={`data-form-table-row ${
                                        hasValidationError ? "error" : ""
                                    }`}
                                    size="medium"
                                    maxLength={255}
                                    onChange={(e) =>
                                        onChangeCharityBeneficiary(index, e.target.value)
                                    }
                                />
                                {index > 0 && (
                                    <DeleteButton
                                        aria-label={`Delete Charity Beneficiary ${index}`}
                                        className="button-delete no-bottom-border"
                                        onDelete={() => onDeleteCharityBeneficiary(index)}
                                        destructive={false}
                                    />
                                )}
                            </div>
                        )
                    )}
                    {hasValidationError && (
                        <div className="input__error input__info margintop-sm">
                            Beneficiary is required.
                        </div>
                    )}
                </div>
            </section>
        );
    }

    return (
        <FormPageSection id={getSectionId(pageIndex, sectionIndex)}
                         distributionStyle={distribution.distributionType}
                         headerText={headerText}
                         showRequiredLabel={true}
                         headerButtonIndex={sectionIndex}
                         headerIcon={icon}
                         showDeleteButton={true}
                         onClickButton={deleteDistribution}>

            <div className="distribution-form" aria-label="Non-Trust Form"
                 data-testid={'non-trust-form-' + sectionIndex}>
                {
                    isOutright ?
                        <MemberDropdownMultiselect
                            label={nameLabelText}
                            required={true}
                            includeNT={true}
                            useTags={true}
                            options={rawFamilyMembers}
                            selectedOptions={(distribution as Outright).outrightMembers ?? []}
                            onChange={(options: any) => onChangeDropdown(options)}
                            onError={
                                saveHasBeenClicked &&
                                (!(distribution as Outright).outrightMembers?.length) ?
                                    nameLabelText + " is required." :
                                    undefined
                            }
                        /> : renderCharityNameInputs()
                }

                <TextAreaField input={distribution.funding}
                               label={Labels.FUNDING_LABEL_TEXT}
                               subText={Labels.FUNDING_SUB_TEXT}
                               onChange={(funding) => setDistribution({...distribution, funding})}/>

                <TextAreaField input={distribution.additionalDetails}
                               label={Labels.ADDITIONAL_DETAILS_LABEL_TEXT}
                               subText={additionalDetailsSubText}
                               onChange={(additionalDetails) => {
                                   setDistribution({...distribution, additionalDetails})
                               }}/>
            </div>
        </FormPageSection>
    );
}

export default NonTrustDistributionForm